/**
 * TTS Profile
 *
 * Canonical voice profile shared across backend + frontend.
 * All env overrides are resolved here only.
 *
 * @module core/tts/ttsProfile
 */

export type TtsOutputFormat = 'mp3' | 'wav';

export interface TtsProfile {
  voice: string;
  playbackRate: number;
  outputFormat: TtsOutputFormat;
  speed?: number;
}

export const DEFAULT_TTS_PROFILE = {
  voice: 'cordelia',
  playbackRate: 1.1,
  outputFormat: 'mp3',
  speed: 1.1,
} as const satisfies TtsProfile;

function readEnv(key: string): string | undefined {
  if (typeof process !== 'undefined' && process?.env) {
    return process.env[key];
  }
  return undefined;
}

function parseNumber(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return parsed;
}

function parseOutputFormat(value: string | undefined): TtsOutputFormat | undefined {
  if (!value) return undefined;
  const normalized = value.toLowerCase();
  if (normalized === 'mp3' || normalized === 'wav') return normalized as TtsOutputFormat;
  return undefined;
}

export function getTtsProfile(): TtsProfile {
  const voice = readEnv('RITHMO_TTS_VOICE');
  const playbackRate = parseNumber(readEnv('RITHMO_TTS_PLAYBACK_RATE'));
  const outputFormat = parseOutputFormat(readEnv('RITHMO_TTS_OUTPUT_FORMAT'));
  const speed = parseNumber(readEnv('RITHMO_TTS_SPEED'));

  const providerDefault =
    readEnv('TTS_PROVIDER')?.toLowerCase() === 'elevenlabs' ? 1.0 : DEFAULT_TTS_PROFILE.speed;

  const resolvedRate =
    speed ??
    playbackRate ??
    providerDefault ??
    DEFAULT_TTS_PROFILE.playbackRate;

  const resolved: TtsProfile = {
    voice: voice ?? DEFAULT_TTS_PROFILE.voice,
    playbackRate: resolvedRate,
    outputFormat: outputFormat ?? DEFAULT_TTS_PROFILE.outputFormat,
  };

  resolved.speed = resolvedRate;

  return resolved;
}
